import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { detailedReviews } from '../data/ReviewsData';


const Reviews: React.FC = () => {

  const totalReviews = detailedReviews.length;
  const averageRating =
    detailedReviews.reduce((acc, r) => acc + (r.rating / r.ratingOutOf) * 10, 0) /
    totalReviews;

  const stats = [
    { label: 'Average Rating', value: averageRating.toFixed(1), icon: Star },
    { label: 'Total Reviews', value: totalReviews, icon: Quote },
    { label: 'Repeat Guests', value: '50%', icon: Star },
  ];

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-earth-brown rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-serif font-bold text-earth-brown mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Reviews */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-serif font-semibold text-earth-brown mb-4">
              Recent Reviews
            </h2>
            <p className="text-gray-600">Detailed feedback from our recent guests</p>
          </motion.div>

          <div className="space-y-8">
            {detailedReviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-8 border border-earth-beige"
              >
                <div className="flex items-start space-x-4">
                  {review.avatar ? (
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-earth-brown flex items-center justify-center text-white text-xl font-bold">
                      {review.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-earth-brown">
                          {review.name}
                        </h3>
                        <p className="text-gray-600 text-sm">{review.location}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center mb-1">
                          {Array.from({ length: 10 }, (_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < Math.round((review.rating / review.ratingOutOf) * 10)
                                  ? 'fill-earth-gold text-earth-gold'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-gray-500 text-sm">{review.date}</p>
                      </div>
                    </div>
                    <blockquote className="text-gray-700 leading-relaxed">
                      "{review.comment}"
                    </blockquote>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="text-center mt-12 italic">
        <p className="text-gray-600">Review data are taken from Booking.com and AirBnB.</p>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
